// Over-the-air bundle updates for the native (Capacitor) app.
//
// The native shell ships with a built-in copy of dist/. This module lets a
// newer dist/ be downloaded and swapped in without a store release, using
// @capgo/capacitor-updater in manual (self-hosted) mode:
//
//   1. notifyAppReady()  — tells the plugin the bundle that just loaded is
//      healthy. If this is NOT called within appReadyTimeout after a new
//      bundle boots, the plugin reverts to the previous bundle on the next
//      launch. That is the whole rollback story, so it runs first.
//   2. check()           — fetch the manifest, decide if the bundle it
//      describes applies to THIS device (platform, native build, newer
//      version), download it if so.
//   3. apply             — `next()` right after download (picked up on the
//      next cold start) and `set()` when the app goes to the background
//      (picked up on the next foreground), so the swap never happens while
//      someone is mid-screen.
//
// Everything is a no-op on web. Failures never throw out of here: a broken
// update path must degrade to "keep running what you have".
//
// Manifest contract (served from S3 for the spike; from the update service
// later — same shape):
//   {
//     "version": "2026102001",          // RH_CLIENT_BUILD of the zipped dist
//     "url": "https://.../2026102001.zip",
//     "requires_native_build": 142,     // min CFBundleVersion / versionCode
//     "platforms": ["ios", "android"],  // optional; omit = both
//     "mandatory": false,               // true = apply immediately
//     "checksum": "..."                 // optional; see README on format
//   }
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { RH_CLIENT_BUILD, LIVE_UPDATE_MANIFEST_URL } from "./constants";
import { refreshIfBuildChanged } from "./jwt-interceptor";

// Re-check at most this often while the app stays alive (iOS keeps apps
// resident for days; resume fires constantly).
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

// Which channel to poll follows IS_STAGING() like every other service URL
// in constants.js. VUE_APP_LIVE_UPDATE_MANIFEST_URL is an escape hatch for
// pointing a local test build at an ngrok tunnel; leave it unset otherwise.
function manifestUrl() {
  return process.env.VUE_APP_LIVE_UPDATE_MANIFEST_URL || LIVE_UPDATE_MANIFEST_URL();
}

let updaterPromise = null;
let lastCheckAt = 0;
let pendingBundleId = null;
let listenersInstalled = false;

function isNative() {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android";
}

function log(...args) {
  console.log("[live-update]", ...args);
}

// Lazy import so the web build never pulls the plugin and a missing native
// implementation (old shell without the plugin) surfaces as a logged
// failure rather than a crash at module-evaluation time.
//
// Resolves to { u: CapacitorUpdater } rather than the plugin itself: a
// Capacitor plugin is a Proxy that answers ANY property name with a native
// call stub, so resolving a promise with it makes the promise machinery
// call `.then` on it -> "CapacitorUpdater.then() is not implemented".
function plugin() {
  if (!updaterPromise) {
    updaterPromise = import("@capgo/capacitor-updater").then((mod) => ({ u: mod.CapacitorUpdater }));
  }
  return updaterPromise;
}

async function nativeBuild() {
  try {
    const info = await App.getInfo();
    const n = parseInt(info.build, 10);
    return isNaN(n) ? 0 : n;
  } catch (e) {
    log("getInfo failed", e);
    return 0;
  }
}

// Decide whether the manifest's bundle should be installed on this device.
// Mirrors the rules the update service will enforce server-side; keeping
// them here too means a mis-registered release can't take down a shell
// that lacks the plugins it needs.
export function bundleApplies(manifest, { platform, nativeBuild, currentVersion }) {
  if (!manifest || !manifest.version || !manifest.url) return { ok: false, why: "malformed manifest" };
  if (Array.isArray(manifest.platforms) && !manifest.platforms.includes(platform)) {
    return { ok: false, why: `not for ${platform}` };
  }
  const requires = parseInt(manifest.requires_native_build, 10) || 0;
  if (nativeBuild < requires) {
    return { ok: false, why: `needs native build ${requires}, have ${nativeBuild}` };
  }
  const next = parseInt(manifest.version, 10);
  const cur = parseInt(currentVersion, 10);
  if (isNaN(next) || isNaN(cur)) return { ok: false, why: "non-numeric version" };
  if (next <= cur) return { ok: false, why: `already on ${cur}` };
  return { ok: true };
}

async function fetchManifest() {
  const url = manifestUrl();
  if (!url) {
    log("no manifest url; skipping");
    return null;
  }
  // cache-bust so a CDN/S3 edge never pins an old manifest on the device
  const res = await fetch(`${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`, {
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`manifest HTTP ${res.status}`);
  return res.json();
}

export async function check({ force = false } = {}) {
  if (!isNative()) return;
  const now = Date.now();
  if (!force && now - lastCheckAt < CHECK_INTERVAL_MS) return;
  lastCheckAt = now;

  try {
    const manifest = await fetchManifest();
    if (!manifest) return;

    const verdict = bundleApplies(manifest, {
      platform: Capacitor.getPlatform(),
      nativeBuild: await nativeBuild(),
      currentVersion: RH_CLIENT_BUILD(),
    });
    if (!verdict.ok) {
      log("no update:", verdict.why);
      return;
    }

    const { u } = await plugin();
    // Already downloaded on an earlier check (e.g. user never backgrounded)?
    const { bundles } = await u.list();
    const existing = (bundles || []).find((b) => b.version === String(manifest.version));
    let bundle = existing;
    if (!bundle) {
      log("downloading", manifest.version);
      const opts = { url: manifest.url, version: String(manifest.version) };
      if (manifest.checksum) opts.checksum = manifest.checksum;
      bundle = await u.download(opts);
    }

    pendingBundleId = bundle.id;
    // Guarantee pickup on the next cold start even if we never see a
    // background event (the plugin also uses this for its own bookkeeping).
    await u.next({ id: bundle.id });
    log("staged", manifest.version, "bundle", bundle.id);

    if (manifest.mandatory) {
      log("mandatory — applying now");
      await u.set({ id: bundle.id }); // reloads the webview
    }
  } catch (e) {
    // A failed check (offline, CDN hiccup, CORS misconfig) shouldn't lock
    // the device out for the debounce window: retry on the next resume.
    lastCheckAt = 0;
    // Honeybadger is global in this app; report but never propagate.
    log("check failed", e);
    try { window.Honeybadger && window.Honeybadger.notify(e, { context: { stage: "live-update.check" } }); } catch (_) { /* noop */ }
  }
}

async function applyPendingOnBackground() {
  if (!pendingBundleId) return;
  try {
    const { u } = await plugin();
    const id = pendingBundleId;
    pendingBundleId = null;
    log("applying staged bundle on background", id);
    await u.set({ id });
  } catch (e) {
    log("set failed", e);
  }
}

// Call once from App.vue created(). Safe to call on web (returns).
export async function initLiveUpdate() {
  if (!isNative() || listenersInstalled) return;
  listenersInstalled = true;

  try {
    const { u } = await plugin();

    // FIRST: prove this bundle booted. Without this the plugin rolls back
    // after appReadyTimeout — which is exactly what we want for a bundle
    // that throws before reaching this line.
    await u.notifyAppReady();

    const cur = await u.current();
    log("running bundle", cur?.bundle?.version, "native", cur?.native, "client build", RH_CLIENT_BUILD());

    // A bundle swap changed RH_CLIENT_BUILD() but the JWT (and RAS's
    // users.last_client_build stamp) still carry the old one; re-mint so the
    // DB reflects this launch. Fire-and-forget, never throws.
    refreshIfBuildChanged().then((did) => { if (did) log("token re-minted for build", RH_CLIENT_BUILD()); });

    u.addListener("downloadFailed", (ev) => log("downloadFailed", ev));
    u.addListener("updateFailed", (ev) => log("updateFailed (rolled back)", ev));
    u.addListener("appReloaded", () => log("appReloaded"));
  } catch (e) {
    // Old shell without the plugin, or plugin misconfigured: run as-is.
    log("plugin unavailable; OTA disabled", e);
    return;
  }

  App.addListener("appStateChange", ({ isActive }) => {
    if (isActive) {
      check();
    } else {
      applyPendingOnBackground();
    }
  });

  check({ force: true });
}
